/**
 * Canonical format for TicketCourses across the entire system.
 *
 * Format A (only supported shape):
 *   TicketCourses[ticketId][instanceIndex][courseId] = selection (string)
 *   For single-ticket RSVPs we use instanceIndex 1.
 *
 * All reads must normalize via normalizeTicketCoursesToFormatA() so that
 * legacy Format B (ticketId -> { courseId: selection }) is converted to Format A.
 * All writes must produce Format A only.
 */

export const DEFAULT_INSTANCE_INDEX = 1;

/** Canonical type: ticketId -> instanceIndex -> courseId -> selection */
export type TicketCoursesFormatA = Record<
  string,
  Record<number, Record<string, string>>
>;

/**
 * Normalize raw TicketCourses from Firestore (Format A or Format B) into Format A only.
 * Format B: ticketId -> { courseId: selection }
 * Format A: ticketId -> { instanceIndex: { courseId: selection } }
 */
export function normalizeTicketCoursesToFormatA(
  raw: unknown
): TicketCoursesFormatA {
  const result: TicketCoursesFormatA = {};
  if (!raw || typeof raw !== 'object') return result;
  const map = raw as Record<string, unknown>;
  for (const [ticketId, ticketData] of Object.entries(map)) {
    const normalized = normalizeTicketDataToFormatA(ticketData);
    if (normalized && Object.keys(normalized).length > 0) {
      result[ticketId] = normalized;
    }
  }
  return result;
}

/**
 * Normalize one ticket's course data to Format A: { [instanceIndex]: { courseId: selection } }.
 * Accepts Format B (flat { courseId: selection }) or Format A (already { 1: { ... } }).
 */
export function normalizeTicketDataToFormatA(
  ticketData: unknown
): Record<number, Record<string, string>> | null {
  if (!ticketData || typeof ticketData !== 'object') return null;
  const obj = ticketData as Record<string, unknown>;
  const firstVal = Object.values(obj)[0];
  if (typeof firstVal === 'string') {
    // Format B (flat): { courseId: selection }
    const flat: Record<string, string> = {};
    for (const [k, v] of Object.entries(obj)) {
      if (typeof v === 'string' && v.trim()) flat[k] = v.trim();
    }
    if (Object.keys(flat).length === 0) return null;
    return { [DEFAULT_INSTANCE_INDEX]: flat };
  }
  // Format A or mixed: { instanceIndex: { courseId: selection } }
  const out: Record<number, Record<string, string>> = {};
  for (const [key, val] of Object.entries(obj)) {
    const idx = Number(key);
    if (!Number.isInteger(idx) || idx < 1) continue;
    if (!val || typeof val !== 'object') continue;
    const courseMap: Record<string, string> = {};
    for (const [cid, sel] of Object.entries(val as Record<string, unknown>)) {
      if (typeof sel === 'string' && sel.trim()) courseMap[cid] = sel.trim();
    }
    if (Object.keys(courseMap).length > 0) out[idx] = courseMap;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Get selection for a course from normalized (Format A) TicketCourses.
 * Uses DEFAULT_INSTANCE_INDEX (1) when instanceIndex is omitted.
 */
export function getSelectionFromFormatA(
  ticketCourses: TicketCoursesFormatA,
  ticketId: string,
  courseId: string,
  instanceIndex: number = DEFAULT_INSTANCE_INDEX
): string {
  const ticketData = ticketCourses[ticketId];
  if (!ticketData) return '';
  const instanceCourses = ticketData[instanceIndex] ?? ticketData[DEFAULT_INSTANCE_INDEX];
  if (!instanceCourses) return '';
  return instanceCourses[courseId] ?? '';
}

/** Type for one ticket's data in Format A: instanceIndex -> courseId -> selection */
export type TicketDataFormatA = Record<number, Record<string, string>>;

/**
 * Return true only when value is already in Format A for a single ticket:
 * object with positive integer keys (instance index) and values that are
 * objects of string (courseId) -> string (selection).
 */
export function isTicketDataFormatA(
  value: unknown
): value is TicketDataFormatA {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const obj = value as Record<string, unknown>;
  for (const [key, val] of Object.entries(obj)) {
    const idx = Number(key);
    if (!Number.isInteger(idx) || idx < 1) return false;
    if (!val || typeof val !== 'object' || Array.isArray(val)) return false;
    for (const [k, v] of Object.entries(val as Record<string, unknown>)) {
      if (typeof k !== 'string' || typeof v !== 'string') return false;
    }
  }
  return true;
}

/**
 * Return true only when value is already in full TicketCourses Format A:
 * object with string keys (ticketId) and values that pass isTicketDataFormatA.
 */
export function isTicketCoursesFormatA(value: unknown): value is TicketCoursesFormatA {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const map = value as Record<string, unknown>;
  for (const ticketData of Object.values(map)) {
    if (!isTicketDataFormatA(ticketData)) return false;
  }
  return true;
}
